/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';

import {
    FauxTextField,
    FlexRow,
    FlexCol,
    Alert,
    Stack,
    Text,
    Placeholder,
    VisualOnlyLabel,
    RadioPillGroup,
    TextBlock,
    HtmlParser,
} from 'sarsaparilla';

import { ModifyCalendarContainer } from '../../containers/OrderDetailsPage/ModifyCalendarContainer';
import GuestCounter from '../common/GuestCounter';
import { getSingularOrPluralText } from '../../utils';

export function ModifyReservationForm({
    bookingError,
    time,
    setTime,
    setCounts,
    guestTypes,
    counts,
    isTourTimesLoading,
    maxTotalGuests,
    tourTimeOptions,
    minimumCountByGuestType,
    maximumCountByGuestType,
    bookThroughoutTourDuration,
}) {
    const showRadioPills = tourTimeOptions?.length > 0 || isTourTimesLoading;
    const maxTotalTitle = getSingularOrPluralText(maxTotalGuests, 'ticket', 'tickets');

    return (
        <Stack space="lg">
            {!!bookingError && (
                <Alert
                    type="error"
                    heading="Sorry, an error occurred when we tried to make the reservation"
                >
                    <TextBlock>
                        <div>
                            <HtmlParser html={bookingError} />
                        </div>
                    </TextBlock>
                </Alert>
            )}
            <FlexRow className="te-date-time-guests-picker">
                <FlexCol md={6}>
                    <Stack space="xxs">
                        <ModifyCalendarContainer />
                    </Stack>
                </FlexCol>
                <FlexCol md={6}>
                    {maxTotalGuests === 1 && guestTypes.length === 1 ? (
                        <>
                            <FauxTextField label="Quantity" value="1 Ticket" />
                            <Alert type="info" className="mt-1">
                                There is a max of 1 ticket allowed
                            </Alert>
                        </>
                    ) : (
                        <Stack space="xxs">
                            <VisualOnlyLabel>Quantity</VisualOnlyLabel>
                            <GuestCounter
                                placeholder="Add Quantity ..."
                                guestTypes={guestTypes}
                                title="Tickets"
                                minTotalTitle="ticket"
                                maxTotalTitle={maxTotalTitle}
                                onCountChange={setCounts}
                                value={counts}
                                maxTotalGuests={maxTotalGuests}
                                minimumCountByGuestType={minimumCountByGuestType}
                                maximumCountByGuestType={maximumCountByGuestType}
                            />
                        </Stack>
                    )}
                </FlexCol>
            </FlexRow>

            {!showRadioPills && (
                <Stack space="xxs">
                    <Text fontWeight="semibold">
                        {bookThroughoutTourDuration ? 'Entry Window' : 'Starting Time'}
                    </Text>
                    <Text color="gray">
                        Select a date above to see a list of times ...
                    </Text>
                </Stack>
            )}

            {showRadioPills && (
                <RadioPillGroup
                    isLoading={isTourTimesLoading}
                    description={
                        bookThroughoutTourDuration ? 'Entry Window' : 'Starting Time'
                    }
                    options={tourTimeOptions}
                    value={time}
                    onChange={setTime}
                />
            )}
        </Stack>
    );
}

ModifyReservationForm.propTypes = {
    bookingError: PropTypes.node,
    tourTimeOptions: PropTypes.array,
    time: PropTypes.string,
    setTime: PropTypes.func,
    setCounts: PropTypes.func,
    guestTypes: PropTypes.array,
    counts: PropTypes.objectOf(PropTypes.number),
    maxTotalGuests: PropTypes.number,
    isTourTimesLoading: PropTypes.bool,
    minimumCountByGuestType: PropTypes.object,
    maximumCountByGuestType: PropTypes.object,
    bookThroughoutTourDuration: PropTypes.bool,
};

export function ModifyReservationFormPlaceholder() {
    return (
        <FlexRow className="te-date-time-guests-picker">
            <FlexCol md="auto">
                <Placeholder height="4rem" />
            </FlexCol>
            <FlexCol md="auto">
                <Placeholder height="4rem" />
            </FlexCol>
            <FlexCol md="auto">
                <Placeholder height="4rem" />
            </FlexCol>
        </FlexRow>
    );
}
